/* eslint-disable @reatom/unit-naming-rule */
import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Data } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  existingInviteCodeAtom,
  openChangeInviteCodeDialogEvent,
  type InviteCode,
} from '@root/entities/referral'
import { reatomUseInviteCodeForm } from '@root/features/referral/atoms/reatom-use-invite-code-form'
import { useTranslation } from '@root/shared/localization'
import { Drawer, Separator } from '@root/shared/ui-kit'

import { InviteCodeForm } from '../../desktop/common/invite-code-form'
import { InviteCodeUserInfo } from '../../desktop/common/invite-code-user-info'

import type { OpenChangeInviteCodeDialogPayload } from '@root/entities/referral'

export const EditInviteCodeDrawer = () => {
  const [payload, setPayload] = useState<
    OpenChangeInviteCodeDialogPayload | undefined
  >(undefined)

  useEffect(
    () =>
      openChangeInviteCodeDialogEvent.onCall((_, p) => {
        setPayload(p)
      }),
    [],
  )

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setPayload(undefined)
    }
  }

  return (
    <AnimatePresence>
      {payload === undefined ? undefined : (
        <Inner
          initialInviteCode={payload.inviteCode}
          onOpenChange={handleOpenChange}
        />
      )}
    </AnimatePresence>
  )
}

type InnerProps = {
  initialInviteCode: InviteCode | undefined
  onOpenChange: (open: boolean) => void
}

const Inner = ({ initialInviteCode, onOpenChange }: InnerProps) => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)
  const [existingInviteCode] = useAtom(existingInviteCodeAtom)

  const model = useMemo(
    () => reatomUseInviteCodeForm({ initialInviteCode, existingInviteCode }),
    [initialInviteCode, existingInviteCode],
  )

  const [showCode] = useAtom(model.showCodeAtom)
  const handleClose = () => onOpenChange(false)

  return (
    <Root open onOpenChange={onOpenChange} backgroundGradient="primary">
      <Content>
        <Header>
          <Title>{t('referral.change-invite-code')}</Title>
          {showCode ? (
            <>
              <SubTitle>
                {Data.match.I(showCode)({
                  ExistingShowCode: () => t('referral.current-code-user-label'),
                  NewShowCode: () => t('referral.new-code-user-label'),
                })}
              </SubTitle>
              <Code>
                <CurrencyNetworkIcon network={network} size={25} />
                {showCode.inviteCode}
              </Code>
            </>
          ) : undefined}

          <InviteCodeUserInfo inviterStateAtom={model.getInviterState} />
        </Header>
        <Body>
          <Separator />
          <Form
            device="mobile"
            model={model}
            existingInviteCode={existingInviteCode}
            onDone={handleClose}
            onCancel={handleClose}
          />
        </Body>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  padding-bottom: var(--safe-bottom);
`
const Form = styled(InviteCodeForm)``
const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  position: relative;
  text-align: center;
  gap: 12px;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  gap: 16px;
  background: radial-gradient(
    106.14% 158.88% at 50% -46.89%,
    var(--backgroundA-4) 0%,
    var(--backgroundA-1) 100%
  );
  backdrop-filter: blur(16px);
`

const SubTitle = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'h3',
})`
  color: var(--textA-9);

  strong {
    color: var(--textA-11);
  }
`

const Title = styled(Typography).attrs({
  variant: 'h4',
  component: 'h2',
})`
  font-size: 18px;
  line-height: 140%;
  color: var(--text-12);
`

const Code = styled(Typography).attrs({
  variant: 'h3',
  component: 'div',
})`
  color: var(--text-12);
  text-align: center;

  display: grid;
  grid-auto-flow: column;
  gap: 4px;

  font-size: 24px;
  line-height: 120%;

  justify-content: center;
  align-content: center;
  align-items: center;
`
